import { useCallback, useEffect, useState } from "react";
import {
 HiOutlineUser,
 HiOutlineMail,
 HiOutlineShieldCheck,
 HiOutlineIdentification,
 HiOutlinePencil,
 HiOutlineX,
 HiOutlineKey,
} from "react-icons/hi";
import { fetchMyProfileDetails, updateMyProfile, changeMyPassword, clearProfileCache } from "../services/userManagementApi";
import Toast from "../components/Toast";

const EMPTY_FORM = { first_name: "", last_name: "", email: "" };

function Profile() {
 const [profile, setProfile] = useState(null);
 const [loading, setLoading] = useState(true);
 const [editing, setEditing] = useState(false);
 const [form, setForm] = useState(EMPTY_FORM);
 const [saving, setSaving] = useState(false);
 const [toast, setToast] = useState(null);
 const [changingPassword, setChangingPassword] = useState(false);
 const [passwordForm, setPasswordForm] = useState({ current_password: "", new_password: "", confirm_password: "" });
 const [savingPassword, setSavingPassword] = useState(false);

 const loadProfile = useCallback(async () => {
 try {
 const res = await fetchMyProfileDetails();
 setProfile(res.data);
 } catch (err) {
 console.error("Failed to fetch profile", err);
 } finally {
 setLoading(false);
 }
 }, []);

 useEffect(() => {
 loadProfile();
 }, [loadProfile]);

 const capitalize = (text) =>
 text ? text.charAt(0).toUpperCase() + text.slice(1).toLowerCase() : "";

 const roleLabels = {
 ADMIN: "ADMIN",
 SPL: "SPL",
 PL: "PL",
 PM: "PM",
 PO: "PO",
 };

 const getRoleLabel = (role) => {
 if (!role) return "";
 return roleLabels[role.toUpperCase()] || capitalize(role);
 };

 const openEdit = () => {
 setForm({
 first_name: profile?.first_name || "",
 last_name: profile?.last_name || "",
 email: profile?.email || "",
 });
 setEditing(true);
 };

 // FastAPI errors: detail can be a string OR a 422 array of {msg, loc, ...}.
 const extractError = (err, fallback) => {
 const d = err?.response?.data?.detail;
 if (typeof d === "string") return d;
 if (Array.isArray(d)) return d.map((e) => e?.msg || "Invalid input").join(", ");
 return fallback;
 };

 const NAME_RE = /^[A-Za-z][A-Za-z .'-]*$/;
 const EMAIL_RE = /^[a-zA-Z0-9_.+-]+@[a-zA-Z0-9-]+\.[a-zA-Z0-9-.]+$/;

 const handleSave = async () => {
 const first = form.first_name.trim();
 const last = form.last_name.trim();
 const email = form.email.trim();
 if (!first || !last || !email) {
 setToast({ type: "error", message: "First name, last name and email are required" });
 return;
 }
 if (!NAME_RE.test(first) || !NAME_RE.test(last)) {
 setToast({ type: "error", message: "First name and last name must contain only letters" });
 return;
 }
 if (!EMAIL_RE.test(email)) {
 setToast({ type: "error", message: "Invalid email format" });
 return;
 }
 setSaving(true);
 try {
 await updateMyProfile({ first_name: first, last_name: last, email });
 clearProfileCache();
 setEditing(false);
 await loadProfile();
 setToast({ type: "success", message: "Profile updated successfully" });
 } catch (err) {
 setToast({ type: "error", message: extractError(err, "Failed to update profile") });
 } finally {
 setSaving(false);
 }
 };

 const openChangePassword = () => {
 setPasswordForm({ current_password: "", new_password: "", confirm_password: "" });
 setChangingPassword(true);
 };

 const handleChangePassword = async () => {
 const { current_password, new_password, confirm_password } = passwordForm;
 if (!current_password || !new_password || !confirm_password) {
 setToast({ type: "error", message: "All password fields are required" });
 return;
 }
 if (new_password.length < 6) {
 setToast({ type: "error", message: "New password must be at least 6 characters" });
 return;
 }
 if (new_password !== confirm_password) {
 setToast({ type: "error", message: "New password and confirmation do not match" });
 return;
 }
 setSavingPassword(true);
 try {
 await changeMyPassword({ current_password, new_password });
 setChangingPassword(false);
 setToast({ type: "success", message: "Password changed successfully" });
 } catch (err) {
 setToast({ type: "error", message: extractError(err, "Failed to change password") });
 } finally {
 setSavingPassword(false);
 }
 };

 if (loading) {
 return (
 <div className="p-6 max-w-3xl mx-auto space-y-6">
 <div className="bg-white rounded-lg border border-[#e5e7eb] p-8 animate-pulse">
 <div className="flex items-center gap-6">
 <div className="w-16 h-16 bg-[#e5e7eb] rounded-lg" />
 <div className="flex-1">
 <div className="h-6 bg-[#e5e7eb] rounded w-48 mb-2" />
 <div className="h-4 bg-[#e5e7eb] rounded w-32 mb-2" />
 <div className="h-4 bg-[#e5e7eb] rounded w-24" />
 </div>
 </div>
 </div>
 </div>
 );
 }

 const displayRole = getRoleLabel(profile?.roles?.[0]) || "User";
 const displayName =
 [profile?.first_name, profile?.last_name].filter(Boolean).join(" ") || profile?.username || "—";
 const avatarLetter =
 (profile?.first_name || profile?.username || "U").charAt(0).toUpperCase();

 return (
 <div className="p-6 max-w-3xl mx-auto space-y-6">
 {/* Profile Header Card */}
 <div className="bg-white rounded-lg border border-[#e5e7eb] overflow-hidden">
 <div className="px-8 py-8">
 {/* Avatar + Name */}
 <div className="flex items-center gap-6">
 <div className="w-16 h-16 bg-[#0078d4] rounded-lg flex items-center justify-center text-2xl font-semibold text-white flex-shrink-0">
 {avatarLetter}
 </div>
 <div className="flex-1 flex items-center justify-between">
 <div>
 <h2 className="m-0 text-lg font-semibold text-[#24292f]">
 {displayName}
 </h2>
 <p className="m-0 mt-1 text-sm text-[#57606a]">
 {profile?.email || "—"}
 </p>
 </div>
 <button
 onClick={openEdit}
 className="inline-flex items-center gap-2 py-2 px-4 text-sm font-medium rounded-md bg-white text-[#24292f] border border-[#d0d5dd] hover:bg-[#f3f4f6] transition-colors duration-150 cursor-pointer">
 <HiOutlinePencil className="text-sm" />
 Edit Profile
 </button>
 </div>
 </div>

 {/* Role Badge */}
 <div className="mt-5 flex items-center gap-3">
 <span className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium bg-[#eff6ff] text-[#0078d4] rounded">
 <HiOutlineShieldCheck className="text-sm" />
 {displayRole}
 </span>
 <span className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium bg-green-50 text-green-700 rounded">
 ● Active
 </span>
 </div>
 </div>
 </div>

 {/* Account Information */}
 <div className="bg-white rounded-lg border border-[#e5e7eb] overflow-hidden">
 <div className="px-8 py-4 border-b border-[#e5e7eb]">
 <h3 className="m-0 text-sm font-semibold text-[#24292f]">Account Information</h3>
 <p className="m-0 mt-1 text-[13px] text-[#8b949e]">Your personal account details</p>
 </div>
 <div className="divide-y divide-[#e5e7eb]">
 <div className="flex items-center gap-4 px-8 py-4 hover:bg-[#f6f8fa] transition-colors">
 <div className="w-9 h-9 bg-[#f3f4f6] rounded-md flex items-center justify-center flex-shrink-0">
 <HiOutlineUser className="text-base text-[#57606a]" />
 </div>
 <div className="flex-1 min-w-0">
 <p className="m-0 text-xs font-medium text-[#8b949e]">First Name</p>
 <p className="m-0 mt-0.5 text-sm font-medium text-[#24292f]">
 {profile?.first_name || "—"}
 </p>
 </div>
 </div>
 <div className="flex items-center gap-4 px-8 py-4 hover:bg-[#f6f8fa] transition-colors">
 <div className="w-9 h-9 bg-[#f3f4f6] rounded-md flex items-center justify-center flex-shrink-0">
 <HiOutlineUser className="text-base text-[#57606a]" />
 </div>
 <div className="flex-1 min-w-0">
 <p className="m-0 text-xs font-medium text-[#8b949e]">Last Name</p>
 <p className="m-0 mt-0.5 text-sm font-medium text-[#24292f]">
 {profile?.last_name || "—"}
 </p>
 </div>
 </div>
 <div className="flex items-center gap-4 px-8 py-4 hover:bg-[#f6f8fa] transition-colors">
 <div className="w-9 h-9 bg-[#f3f4f6] rounded-md flex items-center justify-center flex-shrink-0">
 <HiOutlineIdentification className="text-base text-[#57606a]" />
 </div>
 <div className="flex-1 min-w-0">
 <p className="m-0 text-xs font-medium text-[#8b949e]">Username</p>
 <p className="m-0 mt-0.5 text-sm font-medium text-[#24292f]">
 {profile?.username || "—"}
 </p>
 </div>
 <span className="text-[11px] text-[#8b949e]">Not editable</span>
 </div>
 <div className="flex items-center gap-4 px-8 py-4 hover:bg-[#f6f8fa] transition-colors">
 <div className="w-9 h-9 bg-[#f3f4f6] rounded-md flex items-center justify-center flex-shrink-0">
 <HiOutlineMail className="text-base text-[#57606a]" />
 </div>
 <div className="flex-1 min-w-0">
 <p className="m-0 text-xs font-medium text-[#8b949e]">Email Address</p>
 <p className="m-0 mt-0.5 text-sm font-medium text-[#24292f]">
 {profile?.email || "—"}
 </p>
 </div>
 </div>
 <div className="flex items-center gap-4 px-8 py-4 hover:bg-[#f6f8fa] transition-colors">
 <div className="w-9 h-9 bg-[#f3f4f6] rounded-md flex items-center justify-center flex-shrink-0">
 <HiOutlineShieldCheck className="text-base text-[#57606a]" />
 </div>
 <div className="flex-1 min-w-0">
 <p className="m-0 text-xs font-medium text-[#8b949e]">Role & Permissions</p>
 <p className="m-0 mt-0.5 text-sm font-medium text-[#24292f]">
 {displayRole}
 </p>
 </div>
 <span className="text-[11px] text-[#8b949e]">Not editable</span>
 </div>
 </div>
 </div>

 {/* Edit Profile Modal */}
 {editing && (
 <div className="fixed inset-0 z-[200] flex items-center justify-center bg-black/40 px-4">
 <div className="bg-white rounded-lg border border-[#e5e7eb] w-full max-w-md shadow-xl">
 <div className="flex items-center justify-between px-6 py-4 border-b border-[#e5e7eb]">
 <h3 className="m-0 text-sm font-semibold text-[#24292f]">Edit Profile</h3>
 <button onClick={() => setEditing(false)} className="text-[#8b949e] hover:text-[#57606a] bg-transparent border-none cursor-pointer p-1">
 <HiOutlineX className="text-lg" />
 </button>
 </div>
 <div className="px-6 py-5 space-y-4">
 <div>
 <label className="block text-xs font-medium text-[#24292f] mb-1">First Name</label>
 <input type="text" value={form.first_name}
 onChange={(e) => setForm({ ...form, first_name: e.target.value })}
 className="w-full py-2 px-3 text-sm border border-[#d0d5dd] rounded-md focus:outline-none focus:border-[#0078d4] focus:ring-2 focus:ring-[#0078d4]/10" />
 </div>
 <div>
 <label className="block text-xs font-medium text-[#24292f] mb-1">Last Name</label>
 <input type="text" value={form.last_name}
 onChange={(e) => setForm({ ...form, last_name: e.target.value })}
 className="w-full py-2 px-3 text-sm border border-[#d0d5dd] rounded-md focus:outline-none focus:border-[#0078d4] focus:ring-2 focus:ring-[#0078d4]/10" />
 </div>
 <div>
 <label className="block text-xs font-medium text-[#24292f] mb-1">Email</label>
 <input type="email" value={form.email}
 onChange={(e) => setForm({ ...form, email: e.target.value })}
 className="w-full py-2 px-3 text-sm border border-[#d0d5dd] rounded-md focus:outline-none focus:border-[#0078d4] focus:ring-2 focus:ring-[#0078d4]/10" />
 </div>
 <div>
 <label className="block text-xs font-medium text-[#8b949e] mb-1">Username (not editable)</label>
 <input type="text" value={profile?.username || ""} disabled
 className="w-full py-2 px-3 text-sm border border-[#d0d5dd] rounded-md bg-[#f6f8fa] text-[#8b949e] cursor-not-allowed" />
 </div>
 <div>
 <label className="block text-xs font-medium text-[#8b949e] mb-1">Role (not editable)</label>
 <input type="text" value={displayRole} disabled
 className="w-full py-2 px-3 text-sm border border-[#d0d5dd] rounded-md bg-[#f6f8fa] text-[#8b949e] cursor-not-allowed" />
 </div>
 </div>
 <div className="flex items-center justify-end gap-2 px-6 py-4 border-t border-[#e5e7eb]">
 <button onClick={() => setEditing(false)} disabled={saving}
 className="py-2 px-4 text-sm font-medium rounded-md bg-white text-[#24292f] border border-[#d0d5dd] hover:bg-[#f3f4f6] transition-colors cursor-pointer disabled:opacity-50">
 Cancel
 </button>
 <button onClick={handleSave} disabled={saving}
 className="py-2 px-4 text-sm font-medium rounded-md bg-[#0078d4] text-white border-none hover:bg-[#106ebe] transition-colors cursor-pointer disabled:opacity-50">
 {saving ? "Saving…" : "Save Changes"}
 </button>
 </div>
 </div>
 </div>
 )}

 {/* Change Password Modal */}
 {changingPassword && (
 <div className="fixed inset-0 z-[200] flex items-center justify-center bg-black/40 px-4">
 <div className="bg-white rounded-lg border border-[#e5e7eb] w-full max-w-md shadow-xl">
 <div className="flex items-center justify-between px-6 py-4 border-b border-[#e5e7eb]">
 <h3 className="m-0 text-sm font-semibold text-[#24292f]">Change Password</h3>
 <button onClick={() => setChangingPassword(false)} className="text-[#8b949e] hover:text-[#57606a] bg-transparent border-none cursor-pointer p-1">
 <HiOutlineX className="text-lg" />
 </button>
 </div>
 <div className="px-6 py-5 space-y-4">
 <div>
 <label className="block text-xs font-medium text-[#24292f] mb-1">Current Password</label>
 <input type="password" value={passwordForm.current_password}
 onChange={(e) => setPasswordForm({ ...passwordForm, current_password: e.target.value })}
 className="w-full py-2 px-3 text-sm border border-[#d0d5dd] rounded-md focus:outline-none focus:border-[#0078d4] focus:ring-2 focus:ring-[#0078d4]/10" />
 </div>
 <div>
 <label className="block text-xs font-medium text-[#24292f] mb-1">New Password</label>
 <input type="password" value={passwordForm.new_password}
 onChange={(e) => setPasswordForm({ ...passwordForm, new_password: e.target.value })}
 className="w-full py-2 px-3 text-sm border border-[#d0d5dd] rounded-md focus:outline-none focus:border-[#0078d4] focus:ring-2 focus:ring-[#0078d4]/10" />
 <p className="m-0 mt-1 text-[11px] text-[#8b949e]">Must be at least 6 characters</p>
 </div>
 <div>
 <label className="block text-xs font-medium text-[#24292f] mb-1">Confirm New Password</label>
 <input type="password" value={passwordForm.confirm_password}
 onChange={(e) => setPasswordForm({ ...passwordForm, confirm_password: e.target.value })}
 className="w-full py-2 px-3 text-sm border border-[#d0d5dd] rounded-md focus:outline-none focus:border-[#0078d4] focus:ring-2 focus:ring-[#0078d4]/10" />
 </div>
 </div>
 <div className="flex items-center justify-end gap-2 px-6 py-4 border-t border-[#e5e7eb]">
 <button onClick={() => setChangingPassword(false)} disabled={savingPassword}
 className="py-2 px-4 text-sm font-medium rounded-md bg-white text-[#24292f] border border-[#d0d5dd] hover:bg-[#f3f4f6] transition-colors cursor-pointer disabled:opacity-50">
 Cancel
 </button>
 <button onClick={handleChangePassword} disabled={savingPassword}
 className="py-2 px-4 text-sm font-medium rounded-md bg-[#0078d4] text-white border-none hover:bg-[#106ebe] transition-colors cursor-pointer disabled:opacity-50">
 {savingPassword ? "Changing…" : "Change Password"}
 </button>
 </div>
 </div>
 </div>
 )}

 {toast && <Toast message={toast.message} type={toast.type} onClose={() => setToast(null)} />}

 {/* Security Section */}
 <div className="bg-white rounded-lg border border-[#e5e7eb] overflow-hidden">
 <div className="px-8 py-4 border-b border-[#e5e7eb]">
 <h3 className="m-0 text-sm font-semibold text-[#24292f]">Security</h3>
 <p className="m-0 mt-1 text-[13px] text-[#8b949e]">Manage your security settings and sessions</p>
 </div>
 <div className="divide-y divide-[#e5e7eb]">
 <div className="flex items-center gap-4 px-8 py-4 hover:bg-[#f6f8fa] transition-colors">
 <div className="w-9 h-9 bg-[#f3f4f6] rounded-md flex items-center justify-center flex-shrink-0">
 <HiOutlineKey className="text-base text-[#57606a]" />
 </div>
 <div className="flex-1 min-w-0">
 <p className="m-0 text-xs font-medium text-[#8b949e]">Password</p>
 <p className="m-0 mt-0.5 text-sm text-[#57606a]">
 ••••••••••••
 </p>
 </div>
 <button onClick={openChangePassword} className="text-xs font-medium text-[#0078d4] hover:text-[#106ebe] transition-colors bg-transparent border-none cursor-pointer">
 Change
 </button>
 </div>
 </div>
 </div>

 {/* Security Section - Remaining rows HIDDEN FOR NOW */}
 {/* <div className="bg-white rounded-lg border border-[#e5e7eb] overflow-hidden">
 <div className="divide-y divide-[#e5e7eb]">
 <div className="flex items-center gap-4 px-8 py-4 hover:bg-[#f6f8fa] transition-colors">
 <div className="w-9 h-9 bg-[#f3f4f6] rounded-md flex items-center justify-center flex-shrink-0">
 <HiOutlineClock className="text-base text-[#57606a]" />
 </div>
 <div className="flex-1 min-w-0">
 <p className="m-0 text-xs font-medium text-[#8b949e]">Last Login</p>
 <p className="m-0 mt-0.5 text-sm text-[#57606a]">
 {new Date().toLocaleDateString("en-US", {
 year: "numeric",
 month: "long",
 day: "numeric",
 hour: "2-digit",
 minute: "2-digit",
 })}
 </p>
 </div>
 </div>
 <div className="flex items-center gap-4 px-8 py-4 hover:bg-[#f6f8fa] transition-colors">
 <div className="w-9 h-9 bg-[#f3f4f6] rounded-md flex items-center justify-center flex-shrink-0">
 <HiOutlineFingerPrint className="text-base text-[#57606a]" />
 </div>
 <div className="flex-1 min-w-0">
 <p className="m-0 text-xs font-medium text-[#8b949e]">Two-Factor Auth</p>
 <p className="m-0 mt-0.5 text-sm text-[#57606a]">Not enabled</p>
 </div>
 <button className="text-xs font-medium text-[#0078d4] hover:text-[#106ebe] transition-colors bg-transparent border-none cursor-pointer">
 Enable
 </button>
 </div>
 <div className="flex items-center gap-4 px-8 py-4 hover:bg-[#f6f8fa] transition-colors">
 <div className="w-9 h-9 bg-[#f3f4f6] rounded-md flex items-center justify-center flex-shrink-0">
 <HiOutlineDesktopComputer className="text-base text-[#57606a]" />
 </div>
 <div className="flex-1 min-w-0">
 <p className="m-0 text-xs font-medium text-[#8b949e]">Active Sessions</p>
 <p className="m-0 mt-0.5 text-sm text-[#57606a]">1 active session (current)</p>
 </div>
 <button className="text-xs font-medium text-red-600 hover:text-red-700 transition-colors bg-transparent border-none cursor-pointer">
 Revoke All
 </button>
 </div>
 </div>
 </div> */}

 {/* Danger Zone - HIDDEN FOR NOW */}
 {/* <div className="bg-white rounded-lg border border-red-200 overflow-hidden">
 <div className="px-8 py-4 border-b border-red-200 bg-red-50">
 <h3 className="m-0 text-sm font-semibold text-red-700">Danger Zone</h3>
 <p className="m-0 mt-0.5 text-xs text-red-500">Irreversible and destructive actions</p>
 </div>
 <div className="px-8 py-4 flex items-center justify-between">
 <div>
 <p className="m-0 text-sm font-medium text-[#24292f]">Delete Account</p>
 <p className="m-0 mt-0.5 text-xs text-[#8b949e]">
 Permanently delete your account and all associated data
 </p>
 </div>
 <button className="py-2 px-4 text-sm font-medium rounded-md bg-white text-red-600 border border-red-200 hover:bg-red-50 transition-colors duration-150 cursor-pointer">
 Delete Account
 </button>
 </div>
 </div> */}
 </div>
 );
}

export default Profile;
